import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowRight, Check, X, Loader2, ShieldCheck } from "lucide-react";
import api from "../../api/axios"; // ⚠️ نفس ملاحظة مسار الـ axios instance
import { teacherPermissionsAPI } from "../../api/teacherPermissions";

const MODULES = [
  { key: "IELTS", label: "IELTS", fullAccess: true },
  { key: "STEP", label: "STEP", fullAccess: true },
  { key: "GENERAL", label: "General English", fullAccess: false },
  { key: "ACADEMY", label: "Academy", fullAccess: false },
  { key: "LANGUAGES", label: "Languages", fullAccess: false },
];

export default function TeacherPermissions() {
  const { teacherId } = useParams();
  const navigate = useNavigate();

  const [teacher, setTeacher] = useState(null);
  const [permissions, setPermissions] = useState([]); // من الـ backend
  const [categoriesByModule, setCategoriesByModule] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingModule, setSavingModule] = useState(null);
  const [error, setError] = useState("");
  // { GENERAL: [1, 2], ... } — اختيار الأدمن قبل الحفظ
  const [selected, setSelected] = useState({});

  const permsFor = (moduleKey) =>
    permissions.filter((p) => p.module === moduleKey);

  const replaceModulePerms = (moduleKey, newPerms) =>
    setPermissions((prev) => [
      ...prev.filter((p) => p.module !== moduleKey),
      ...newPerms,
    ]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [teacherRes, permsRes] = await Promise.all([
        api.get(`/auth/teachers/${teacherId}/`),
        teacherPermissionsAPI.list(teacherId),
      ]);
      setTeacher(teacherRes.data.data);
      setPermissions(permsRes.data || []);

      const categoryModules = MODULES.filter((m) => !m.fullAccess);
      const results = await Promise.all(
        categoryModules.map((m) =>
          teacherPermissionsAPI
            .moduleCategories(m.key)
            .then((res) => [m.key, res.categories || []])
            .catch(() => [m.key, []])
        )
      );
      setCategoriesByModule(Object.fromEntries(results));
    } catch (err) {
      setError("تعذر تحميل بيانات المدرس أو الصلاحيات");
    } finally {
      setLoading(false);
    }
  }, [teacherId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggleFullAccess = async (moduleKey) => {
    const has = permsFor(moduleKey).length > 0;
    setSavingModule(moduleKey);
    setError("");
    try {
      if (has) {
        await teacherPermissionsAPI.remove(teacherId, moduleKey);
        replaceModulePerms(moduleKey, []);
      } else {
        const res = await teacherPermissionsAPI.set(teacherId, moduleKey, []);
        replaceModulePerms(moduleKey, res.data);
      }
    } catch (err) {
      setError("حدث خطأ أثناء تحديث الصلاحية");
    } finally {
      setSavingModule(null);
    }
  };

  const toggleCategory = (moduleKey, id, currentIds) =>
    setSelected((prev) => {
      const base = prev[moduleKey] ?? currentIds;
      return {
        ...prev,
        [moduleKey]: base.includes(id)
          ? base.filter((x) => x !== id)
          : [...base, id],
      };
    });

  const clearSelected = (moduleKey) =>
    setSelected((prev) => {
      const n = { ...prev };
      delete n[moduleKey];
      return n;
    });

  const handleSaveCategories = async (moduleKey, ids) => {
    setSavingModule(moduleKey);
    setError("");
    try {
      if (ids.length === 0) {
        await teacherPermissionsAPI.remove(teacherId, moduleKey);
        replaceModulePerms(moduleKey, []);
      } else {
        const res = await teacherPermissionsAPI.set(teacherId, moduleKey, ids);
        replaceModulePerms(moduleKey, res.data);
      }
      clearSelected(moduleKey);
    } catch (err) {
      setError("حدث خطأ أثناء حفظ الصلاحية");
    } finally {
      setSavingModule(null);
    }
  };

  const handleRemoveOne = async (moduleKey, categoryId) => {
    setSavingModule(moduleKey);
    setError("");
    try {
      await teacherPermissionsAPI.remove(teacherId, moduleKey, categoryId);
      setPermissions((prev) =>
        prev.filter(
          (p) => !(p.module === moduleKey && p.object_id === categoryId)
        )
      );
      clearSelected(moduleKey);
    } catch (err) {
      setError("حدث خطأ أثناء سحب الصلاحية");
    } finally {
      setSavingModule(null);
    }
  };

  if (loading) {
    return (
      <div style={styles.center}>
        <Loader2 className="spin" size={28} color="var(--accent)" />
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      <button style={styles.backBtn} onClick={() => navigate(-1)}>
        <ArrowRight size={16} />
        رجوع
      </button>

      <div style={styles.header}>
        <div style={styles.avatar}>
          <ShieldCheck size={20} color="var(--accent)" />
        </div>
        <div>
          <div style={styles.title}>
            صلاحيات {teacher?.full_name || "المدرس"}
          </div>
          <div style={styles.subtitle}>{teacher?.email}</div>
        </div>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      <div style={styles.cardsGrid}>
        {MODULES.map((mod) => {
          const isSaving = savingModule === mod.key;

          // ───── موديول Full Access (IELTS / STEP) ─────
          if (mod.fullAccess) {
            const existing = permsFor(mod.key).length > 0;
            return (
              <div key={mod.key} style={styles.card}>
                <div style={styles.cardHeader}>
                  <span style={styles.cardTitle}>{mod.label}</span>
                  <span
                    style={{
                      ...styles.badge,
                      ...(existing ? styles.badgeOn : styles.badgeOff),
                    }}
                  >
                    {existing ? "مفعّل" : "غير مفعّل"}
                  </span>
                </div>
                <p style={styles.cardDesc}>Full Access — بدون كاتيجوري محددة</p>
                <button
                  onClick={() => handleToggleFullAccess(mod.key)}
                  disabled={isSaving}
                  style={existing ? styles.dangerBtn : styles.primaryBtn}
                >
                  {isSaving ? (
                    <Loader2 className="spin" size={14} />
                  ) : existing ? (
                    <>
                      <X size={14} /> سحب الصلاحية
                    </>
                  ) : (
                    <>
                      <Check size={14} /> تفعيل الصلاحية
                    </>
                  )}
                </button>
              </div>
            );
          }

          // ───── موديول بيحتاج كاتيجوريز (GENERAL / ACADEMY / LANGUAGES) ─────
          const modPerms = permsFor(mod.key);
          const currentIds = modPerms.map((p) => p.object_id);
          const selectedIds = selected[mod.key] ?? currentIds;
          const dirty =
            selectedIds.length !== currentIds.length ||
            selectedIds.some((id) => !currentIds.includes(id));

          const categories = categoriesByModule[mod.key] || [];
          const grouped =
            mod.key === "LANGUAGES"
              ? categories.reduce((acc, c) => {
                  const key = c.language_name || "غير محدد";
                  (acc[key] = acc[key] || []).push(c);
                  return acc;
                }, {})
              : { "": categories };

          return (
            <div key={mod.key} style={styles.card}>
              <div style={styles.cardHeader}>
                <span style={styles.cardTitle}>{mod.label}</span>
                {modPerms.length > 0 && (
                  <span style={{ ...styles.badge, ...styles.badgeOn }}>
                    {modPerms.length}
                  </span>
                )}
              </div>
              <p style={styles.cardDesc}>حدد الكاتيجوريز اللي المدرس يديرها</p>

              {modPerms.length > 0 && (
                <div style={styles.chips}>
                  {modPerms.map((p) => (
                    <span key={p.id} style={styles.chip}>
                      {p.category_name}
                      <button
                        style={styles.chipX}
                        disabled={isSaving}
                        onClick={() => handleRemoveOne(mod.key, p.object_id)}
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <div style={styles.checklist}>
                {Object.entries(grouped).map(([groupName, cats]) => (
                  <div key={groupName}>
                    {groupName && (
                      <div style={styles.groupLabel}>{groupName}</div>
                    )}
                    {cats.map((c) => (
                      <label key={c.id} style={styles.checkItem}>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(c.id)}
                          onChange={() =>
                            toggleCategory(mod.key, c.id, currentIds)
                          }
                        />
                        {c.name}
                      </label>
                    ))}
                  </div>
                ))}
              </div>

              <button
                onClick={() => handleSaveCategories(mod.key, selectedIds)}
                disabled={isSaving || !dirty}
                style={styles.primaryBtn}
              >
                {isSaving ? (
                  <Loader2 className="spin" size={14} />
                ) : (
                  <Check size={14} />
                )}
                حفظ
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  wrap: { maxWidth: 900, margin: "0 auto" },
  center: { display: "flex", justifyContent: "center", padding: "60px 0" },
  backBtn: {
    display: "flex", alignItems: "center", gap: 6,
    background: "var(--menu-btn-bg)", border: "1px solid var(--glass-border)",
    borderRadius: 10, padding: "8px 14px", color: "var(--text-primary)",
    cursor: "pointer", marginBottom: 20, fontSize: 13.5,
  },
  header: {
    display: "flex", alignItems: "center", gap: 14, marginBottom: 24,
  },
  avatar: {
    width: 44, height: 44, borderRadius: "50%",
    background: "linear-gradient(135deg, rgba(79,124,255,0.15), rgba(139,92,246,0.15))",
    border: "1.5px solid rgba(79,124,255,0.2)",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  title: { fontSize: 18, fontWeight: 700, color: "var(--text-primary)" },
  subtitle: { fontSize: 13, color: "var(--text-secondary)", marginTop: 2 },
  errorBox: {
    background: "var(--danger-bg)", color: "var(--danger)",
    padding: "10px 14px", borderRadius: 10, marginBottom: 16, fontSize: 13.5,
  },
  cardsGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: 16,
  },
  card: {
    background: "var(--glass-bg)", border: "1px solid var(--glass-border)",
    borderRadius: 14, padding: 18, backdropFilter: "blur(20px)",
  },
  cardHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    marginBottom: 6,
  },
  cardTitle: { fontSize: 14.5, fontWeight: 700, color: "var(--text-primary)" },
  cardDesc: { fontSize: 12.5, color: "var(--text-secondary)", margin: "0 0 14px" },
  badge: { fontSize: 11, padding: "3px 9px", borderRadius: 99, fontWeight: 600 },
  badgeOn: { background: "rgba(34,197,94,0.15)", color: "#22c55e" },
  badgeOff: { background: "rgba(148,163,184,0.15)", color: "var(--text-secondary)" },

  // ── جديد: chips + checklist ──
  chips: { display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 },
  chip: {
    display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12,
    padding: "3px 6px 3px 10px", borderRadius: 99,
    background: "rgba(34,197,94,0.15)", color: "#22c55e", fontWeight: 600,
  },
  chipX: {
    background: "none", border: "none", color: "inherit",
    cursor: "pointer", display: "flex", padding: 2,
  },
  checklist: {
    maxHeight: 180, overflowY: "auto", marginBottom: 12, padding: 8,
    border: "1px solid var(--glass-border)", borderRadius: 10,
  },
  groupLabel: {
    fontSize: 11.5, fontWeight: 700, color: "var(--text-secondary)",
    margin: "6px 0 4px",
  },
  checkItem: {
    display: "flex", alignItems: "center", gap: 8, fontSize: 13,
    padding: "4px 0", color: "var(--text-primary)", cursor: "pointer",
  },

  primaryBtn: {
    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
    background: "linear-gradient(135deg, var(--accent), var(--accent-2))",
    color: "#fff", border: "none", borderRadius: 10, padding: "8px 14px",
    fontSize: 13, fontWeight: 600, cursor: "pointer", flex: 1, width: "100%",
  },
  dangerBtn: {
    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
    background: "var(--danger-bg)", color: "var(--danger)",
    border: "1px solid rgba(239,68,68,0.25)", borderRadius: 10, padding: "8px 14px",
    fontSize: 13, fontWeight: 600, cursor: "pointer", flex: 1, width: "100%",
  },
};